import { AssetDto } from '@nusign/picflow-backend-types';
import { Rect } from '../../classes/rect.class';
import { LayoutGridService } from '../services/layout-grid.service';
import { LayoutMosaicColumnsService } from '../services/layout-mosaic-columns.service';
import { LayoutMosaicRowsService } from '../services/layout-mosaic-rows.service';

const getMoveDownStartId = (
  targetAssetId: string,
  assets: AssetDto[],
  assetPositions: {
    bounds: Rect;
    dimensions: Rect;
  }[],
  position: 'before' | 'after',
  layoutService: LayoutGridService | LayoutMosaicRowsService | LayoutMosaicColumnsService,
  showFilenames: boolean
) => {
  if (position === 'before') return targetAssetId;
  const assetAfter = layoutService.getAssetAfter(targetAssetId, assets, assetPositions, showFilenames);
  if (!assetAfter) return;
  return assetAfter.id;
};

const getAssetsToMoveDown = (
  targetAssetId: string,
  assets: AssetDto[],
  assetPositions: {
    bounds: Rect;
    dimensions: Rect;
  }[],
  position: 'before' | 'after',
  layoutService: LayoutGridService | LayoutMosaicRowsService | LayoutMosaicColumnsService,
  showFilenames: boolean
) => {
  const moveDownStartId = getMoveDownStartId(
    targetAssetId,
    assets,
    assetPositions,
    position,
    layoutService,
    showFilenames
  );
  if (!moveDownStartId) return [];
  const toMoveDownIds = [moveDownStartId];
  for (let i = 0; i < 10; i++) {
    const lastId = toMoveDownIds[toMoveDownIds.length - 1];
    const nextId = layoutService.getAssetAfter(lastId, assets, assetPositions, showFilenames)?.id;
    if (!nextId) break;
    toMoveDownIds.push(nextId);
  }
  return toMoveDownIds;
};

const getAssetsToMoveUp = (
  assetId: string,
  assets: AssetDto[],
  assetPositions: {
    bounds: Rect;
    dimensions: Rect;
  }[],
  layoutService: LayoutGridService | LayoutMosaicRowsService | LayoutMosaicColumnsService,
  showFilenames: boolean
) => {
  const moveUpStartId = layoutService.getAssetAfter(assetId, assets, assetPositions, showFilenames)?.id;
  if (!moveUpStartId) return [];
  const toMoveUpIds = [moveUpStartId];
  for (let i = 0; i < 10; i++) {
    const lastId = toMoveUpIds[toMoveUpIds.length - 1];
    const nextId = layoutService.getAssetAfter(lastId, assets, assetPositions, showFilenames)?.id;
    if (!nextId) break;
    toMoveUpIds.push(nextId);
  }
  return toMoveUpIds;
};

const getNewPositionsFromBoundsForVertical = (
  assetBounds: { id: string; bounds: Rect }[]
): { id: string; index: number; newBounds: Rect }[] => {
  return assetBounds
    .sort((a, b) => {
      if (a.bounds.y !== b.bounds.y) return a.bounds.y - b.bounds.y;
      return a.bounds.x - b.bounds.x;
    })
    .map((item, index) => {
      return { id: item.id, index, newBounds: item.bounds, newDimensions: item.bounds };
    });
};

const adjustBottomBounds = (list: { id: string; bounds: Rect }[]) => {
  const columns = list.reduce(
    (acc, item) => {
      const columnX = item.bounds.x;
      if (!acc.some(a => a.x === columnX))
        acc.push({
          x: columnX,
          rects: [],
          height: 0,
        });
      const column = acc.find(a => a.x === columnX);
      column.rects.push(item.bounds);
      column.height += item.bounds.height;
      return acc;
    },
    [] as { x: number; rects: Rect[]; height: number }[]
  );

  columns.forEach(column => {
    column.rects.sort((a, b) => a.y - b.y);
  });

  while (true) {
    const tallestColumnHeight = Math.max(...columns.map(c => c.height));
    const shortestColumnHeight = Math.min(...columns.map(c => c.height));
    const tallestColumn = columns.find(c => c.height === tallestColumnHeight);
    const shortestColumn = columns.find(c => c.height === shortestColumnHeight);
    if (!tallestColumn || !shortestColumn) break;

    const lastAssetInTallestColumn = tallestColumn.rects[tallestColumn.rects.length - 1];
    if (tallestColumnHeight - lastAssetInTallestColumn.height > shortestColumnHeight) {
      const lastAssetInShortestColumn = shortestColumn.rects[shortestColumn.rects.length - 1];
      lastAssetInTallestColumn.y = lastAssetInShortestColumn.y + lastAssetInShortestColumn.height;
      lastAssetInTallestColumn.x = lastAssetInShortestColumn.x;
      shortestColumn.rects.push(lastAssetInTallestColumn);
      tallestColumn.rects.pop();
      tallestColumn.height -= lastAssetInTallestColumn.height;
      shortestColumn.height += lastAssetInTallestColumn.height;
    } else {
      break;
    }
  }
};

const calcHeight = (assetPositions: { bounds: Rect; dimensions: Rect }[], sourceAspectRatio: number): number => {
  if (!assetPositions.length) return 0;
  const firstAsset = assetPositions[0];
  const spacing = firstAsset.bounds.height - firstAsset.dimensions.height;
  const height = firstAsset.dimensions.width * sourceAspectRatio;
  return Math.round(height + spacing);
};

const getReplacedBounds = (
  position: 'before' | 'after',
  targetAssetId: string,
  assets: AssetDto[],
  assetPositions: { bounds: Rect; dimensions: Rect }[],
  layoutService: LayoutGridService | LayoutMosaicRowsService | LayoutMosaicColumnsService,
  showFilenames: boolean,
  newBounds: { id: string; bounds: Rect }[]
) => {
  if (position === 'before') {
    const item = newBounds.find(b => b.id === targetAssetId);
    if (!item) {
      console.warn(targetAssetId, newBounds);
    }
    return item.bounds;
  }

  const replacedId = layoutService.getAssetAfter(targetAssetId, assets, assetPositions, showFilenames)?.id;
  if (replacedId) {
    return newBounds.find(b => b.id === replacedId).bounds;
  }
  const lastBounds = newBounds.find(b => b.id === targetAssetId).bounds;
  return new Rect(lastBounds.x, lastBounds.y + lastBounds.height, lastBounds.width, lastBounds.height);
};

const calculateAssetMovePositionsVertical = (
  assetToMoveId: string,
  targetAssetId: string,
  assets: AssetDto[],
  assetPositions: {
    bounds: Rect;
    dimensions: Rect;
  }[],
  position: 'before' | 'after' | 'out',
  layoutService: LayoutGridService | LayoutMosaicRowsService | LayoutMosaicColumnsService,
  sourceAspectRatio: number,
  showFilenames: boolean
): { id: string; index: number; newBounds: Rect }[] => {
  const assetIndex = assets.findIndex(a => a.id === assetToMoveId);
  const height = calcHeight(assetPositions, sourceAspectRatio);

  const assetsToMoveDown =
    position === 'out'
      ? []
      : getAssetsToMoveDown(targetAssetId, assets, assetPositions, position, layoutService, showFilenames);
  const assetsToMoveUp =
    assetIndex === -1 ? [] : getAssetsToMoveUp(assetToMoveId, assets, assetPositions, layoutService, showFilenames);

  const newBounds = assetPositions.map((pos, index) => {
    const asset = assets[index];
    return {
      id: asset.id,
      bounds: pos.bounds.copy(),
    };
  });

  if (position === 'out') {
    newBounds.splice(assetIndex, 1);
  } else {
    const replacedBounds = getReplacedBounds(
      position,
      targetAssetId,
      assets,
      assetPositions,
      layoutService,
      showFilenames,
      newBounds
    );

    if (assetIndex === -1) {
      newBounds.push({
        id: assetToMoveId,
        bounds: replacedBounds.copy(),
      });
    } else {
      // if the asset that is being moved, is in the same column as the target asset, and the target asset is lower than the moved asset,
      // then we need to subtract the height of the moved asset from its new position
      const isInSameColumn = newBounds[assetIndex].bounds.x === replacedBounds.x;
      const movingDownInSameColumn = isInSameColumn ? replacedBounds.y > newBounds[assetIndex].bounds.y : false;
      const deltaY = movingDownInSameColumn ? newBounds[assetIndex].bounds.height : 0;

      newBounds[assetIndex].bounds.y = replacedBounds.y - deltaY;
      newBounds[assetIndex].bounds.x = replacedBounds.x;
    }
  }

  assetsToMoveDown.forEach(id => {
    if (id === assetToMoveId) return;
    newBounds.find(b => b.id === id).bounds.y += height;
  });

  assetsToMoveUp.forEach(id => {
    if (id === assetToMoveId) return;
    newBounds.find(b => b.id === id).bounds.y -= height;
  });

  adjustBottomBounds(newBounds);

  return getNewPositionsFromBoundsForVertical(newBounds);
};

const calculateAssetMovePositionsHorizontal = (
  assetToMoveId: string,
  targetAssetId: string,
  assets: AssetDto[],
  position: 'before' | 'after' | 'out'
): { id: string; index: number; newBounds: Rect }[] => {
  const currentIndex = assets.findIndex(a => a.id === assetToMoveId);
  let newIndex = assets.findIndex(a => a.id === targetAssetId) + (position === 'after' ? 1 : 0);
  const newAssetsOrder = [...assets];

  // remove asset from list
  if (currentIndex !== -1) {
    newAssetsOrder.splice(currentIndex, 1);
    if (currentIndex < newIndex) {
      // if we removed an asset before the target, we need to adjust the index
      newIndex--;
    }
  }
  if (position !== 'out') {
    // insert asset at new position
    newAssetsOrder.splice(newIndex, 0, <AssetDto>{ id: assetToMoveId });
  }

  return newAssetsOrder.map((a, index) => ({
    id: a.id,
    index,
    newBounds: new Rect(),
  }));
};

export const calculateAssetMovePositions = (
  assetToMoveId: string,
  targetAssetId: string,
  assets: AssetDto[],
  assetPositions: {
    bounds: Rect;
    dimensions: Rect;
  }[],
  layout: 'columns' | 'rows' | 'grid',
  position: 'before' | 'after' | 'out',
  layoutService: LayoutGridService | LayoutMosaicRowsService | LayoutMosaicColumnsService,
  sourceAspectRatio: number,
  showFilenames
): { id: string; index: number; newBounds: Rect }[] => {
  if (layout === 'columns') {
    return calculateAssetMovePositionsVertical(
      assetToMoveId,
      targetAssetId,
      assets,
      assetPositions,
      position,
      layoutService,
      sourceAspectRatio,
      showFilenames
    );
  }
  return calculateAssetMovePositionsHorizontal(assetToMoveId, targetAssetId, assets, position);
};
